import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../ui/sheet';
import {
  NavigationMenu, NavigationMenuContent, NavigationMenuItem,
  NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger,
} from '../ui/navigation-menu';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Menu,
  ShoppingCart,
  User,
  LogOut,
  LayoutDashboard,
  ChevronDown,
  Rocket,
  Bot,
  Cake,
  GraduationCap,
  MapPin,
  Phone,
  BookOpen,
  HelpCircle,
  Heart,
  Target,
  Users,
  Newspaper, Star, Handshake, Cpu, Lightbulb, Shield, Wrench,
  Globe, Stethoscope, Activity, Dna, Bug, FlaskConical, Telescope,
  Code, Terminal, Brain, Gamepad2, PenTool, Building2, Cog, Printer,
  Calculator, Trophy, Trees, School, Eye,
} from 'lucide-react';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
];

export default function MainHeader({ compact = false }) {
  const { isAuthenticated, user, logout, isAdmin, isInstructor, isPartner, isVolunteer } = useAuth();
  const { itemCount } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpandedMenu, setMobileExpandedMenu] = useState(null);

  const handleViewAllCourses = (e) => {
    // The header's primary CTA points parents at Summer Camps (the highest-
    // converting catalog right now). Function name kept for back-compat with
    // existing testids and click handlers; the destination is /summer-camps.
    e.preventDefault();
    if (location.pathname === '/summer-camps') {
      window.location.href = '/summer-camps';
    } else {
      navigate('/summer-camps');
    }
  };

  // Clean, parent-focused navigation
  const defaultNavLinks = [
    { href: '/', label: 'Home' },
    { href: '/catalog', label: 'Programs', hasDropdown: true, dropdownId: 'programs' },
    { href: '/summer-camps', label: 'Summer Camps', highlight: true, highlightColor: 'camps' },
    { href: '/park-district-camps', label: 'Park Districts', highlight: true, highlightColor: 'parks' },
    { href: '/locations', label: 'Locations' },
    { href: '/partners', label: 'Partners' },
    { href: '/about', label: 'About', hasDropdown: true, dropdownId: 'about' },
  ];

  const [headerLocations, setHeaderLocations] = useState([]);
  const [partnerData, setPartnerData] = useState({ districts: [], parks: [], libraries: [] });
  useEffect(() => {
    axios.get(`${API_URL}/api/locations/?page_size=200`).then(res => {
      const locs = res.data?.results || res.data || [];
      setHeaderLocations(locs.filter(l => l.active));
    }).catch(() => {});
    // Fetch partner data for Partners dropdown
    Promise.all([
      axios.get(`${API_URL}/api/partner/districts/`).catch(() => ({ data: { results: [] } })),
      axios.get(`${API_URL}/api/partner/schools/`).catch(() => ({ data: { results: [] } })),
      axios.get(`${API_URL}/api/partner/park-districts/`).catch(() => ({ data: { results: [] } })),
    ]).then(([distRes, schoolRes, parkRes]) => {
      const districts = distRes.data?.results || distRes.data || [];
      const schools = schoolRes.data?.results || schoolRes.data || [];
      const parks = parkRes.data?.results || parkRes.data || [];
      // Group schools by district
      // Group schools by district, ordered: SD204, SD308, SD129, SD131
      const districtOrder = ['SD204', 'SD308', 'SD129', 'SD131'];
      const groupedDistricts = districts
        .map(d => ({
          ...d,
          schools: schools.filter(s => s.district === d.id),
        }))
        .sort((a, b) => {
          const aIdx = districtOrder.indexOf(a.code);
          const bIdx = districtOrder.indexOf(b.code);
          return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
        });
      setPartnerData({ districts: groupedDistricts, parks, libraries: [] });
    }).catch(() => {});
  }, []);

  // Color mapping for category pills
  const pillStyles = {
    science: 'bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25',
    technology: 'bg-sky-500/15 text-sky-200 hover:bg-sky-500/25',
    robotics: 'bg-orange-500/15 text-orange-200 hover:bg-orange-500/25',
    engineering: 'bg-amber-500/15 text-amber-200 hover:bg-amber-500/25',
    math: 'bg-violet-500/15 text-violet-200 hover:bg-violet-500/25',
    camps: 'bg-rose-500/15 text-rose-200 hover:bg-rose-500/25 border border-rose-400/30',
    parks: 'bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 border border-emerald-400/30',
    afterschool: 'bg-blue-500/15 text-blue-200 hover:bg-blue-500/25 border border-blue-400/30',
  };

  const divisionColors = {
    'Science': 'science',
    'Technology': 'technology',
    'Engineering': 'engineering',
    'Math': 'math',
    'Summer Camps': 'camps',
    'Coding': 'technology',
    'Robotics': 'robotics',
  };

  const navLinks = defaultNavLinks;

  const apiDropdownMenus = {};

  // Dropdown menu content definitions (fallback)
  const dropdownMenus = {
    programs: [
      { href: '/science', label: 'Science', icon: FlaskConical, description: 'Medical science, chemistry, space, biology' },
      { href: '/technology', label: 'Technology', icon: Code, description: 'Coding, web dev, apps, cybersecurity, AI' },
      { href: '/engineering', label: 'Engineering', icon: Cog, description: '3D printing, bridges, solar cars, architecture' },
      { href: '/math', label: 'Math', icon: Calculator, description: 'Competition math, puzzles, geometry, logic' },
      { href: '/robotics', label: 'Robotics', icon: Bot, description: 'LEGO, battle bots, rescue bots, AI robots' },
      { href: '/catalog', label: 'All 40 Courses', icon: BookOpen, description: 'Browse the full course catalog' },
    ],
    about: [
      { href: '/about', label: 'About Ready Set STEM', icon: Heart, description: 'Our mission & values' },
      { href: '/our-story', label: 'Our Story', icon: Target, description: 'How it all started' },
      { href: '/community-impact', label: 'Community Impact', icon: Users, description: 'APS Training Academy & giving back' },
      { href: '/blog', label: 'Blog', icon: Newspaper, description: 'News & STEM tips' },
      { href: '/showcase', label: 'Student Showcase', icon: Star, description: 'See what kids are building' },
      { href: '/leaderboard', label: 'Leaderboard', icon: Trophy, description: 'Top STEM achievers' },
      { href: '/faq', label: 'FAQ', icon: HelpCircle, description: 'Common questions' },
      { href: '/contact', label: 'Contact Us', icon: Phone, description: 'Get in touch' },
    ],
  };

  const getDashboardLink = () => {
    if (isAdmin) return '/admin';
    if (isInstructor) return '/instructor';
    if (isVolunteer) return '/instructor';
    if (isPartner) return '/partner';
    return '/parent';
  };

  const getDashboardLabel = () => {
    if (isAdmin) return 'Admin Dashboard';
    if (isInstructor) return 'Instructor Dashboard';
    if (isVolunteer) return 'Volunteer Dashboard';
    if (isPartner) return 'Partner Portal';
    return 'My Dashboard';
  };

  return (
    <header className="sticky top-0 z-50" role="banner">
      {/* Top Bar — Logo row */}
      {!compact && (
        <div className="rsc-header-nav">
          <div className="container-app flex items-center justify-between py-3 md:py-6">
            {/* Left: STEM Accreditation Badge */}
            <div className="flex-shrink-0">
              <img 
                src="/brand/stem-accredited.png" 
                alt="STEM.org Accredited" 
                className="h-10 sm:h-14 md:h-20 w-auto"
              />
            </div>

            {/* Center: Logo */}
            <Link to="/" className="absolute left-1/2 -translate-x-1/2 flex items-center" data-testid="logo-link">
              <img 
                src="/brand/rss-logo-horizontal.png" 
                alt="Ready Set STEM" 
                className="hidden sm:block h-14 md:h-24 lg:h-28 w-auto"
              />
              <img 
                src="/brand/rss-logo.png" 
                alt="Ready Set STEM" 
                className="block sm:hidden h-14 w-auto"
              />
            </Link>

            {/* Right: CTA + Language */}
            <div className="flex items-center gap-2 md:gap-3">
              <a href="/summer-camps" onClick={handleViewAllCourses} className="hidden sm:block">
                <Button className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright font-bold text-sm md:text-base rounded-full px-4 md:px-6 py-2 md:py-3 shadow-md hover:shadow-lg transition-all" data-testid="view-all-courses-btn">
                  Summer Camps
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Compact mode top bar */}
      {compact && (
        <div className="bg-rsc-navy">
          <div className="container-app flex items-center justify-between py-1.5">
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img 
                src="/brand/rss-logo-horizontal.png" 
                alt="Ready Set STEM" 
                className="hidden sm:block h-8 md:h-10 w-auto"
              />
              <img 
                src="/brand/rss-logo.png" 
                alt="Ready Set STEM" 
                className="block sm:hidden h-8 w-auto"
              />
            </Link>
            <div className="flex items-center gap-2">
              <a href="/summer-camps" onClick={handleViewAllCourses}>
                <Button className="bg-rsc-navy-dark hover:bg-rsc-navy text-white rounded-full px-3 py-1 text-xs font-bold border border-white/20" data-testid="view-all-courses-compact-btn">
                  Summer Camps
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Bar */}
      <div className="rsc-header-nav">
        <div className="container-app">
          <nav className={`flex items-center justify-between ${compact ? 'h-11' : 'h-14'}`} role="navigation" aria-label="Main navigation">
            {/* Desktop Nav */}
            <div className="hidden lg:block flex-1">
              <NavigationMenu className="max-w-none">
                <NavigationMenuList className="gap-0.5">
                  {navLinks.map((link) => {
                    const isActive = location.pathname === link.href;
                    const pillClass = link.highlight && link.highlightColor
                      ? pillStyles[link.highlightColor] || ''
                      : '';

                    return (
                      <NavigationMenuItem key={link.href}>
                        {link.hasDropdown ? (
                          <>
                            <NavigationMenuTrigger 
                              className={`bg-transparent hover:bg-white/10 text-white data-[state=open]:bg-white/15 text-base h-10 px-4 ${
                                isActive ? 'text-rsc-yellow' : ''
                              }`}
                              onClick={(e) => {
                                if (e.target.tagName !== 'svg' && !e.target.closest('svg')) {
                                  navigate(link.href);
                                }
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              {link.label}
                            </NavigationMenuTrigger>
                            <NavigationMenuContent>
                              {link.dropdownId === 'partners' ? (
                                /* Partners: grouped by type */
                                <div className="p-4 w-[420px]">
                                  {/* School Districts */}
                                  {partnerData.districts.length > 0 && (
                                    <div className="mb-3">
                                      <div className="flex items-center gap-2 px-1 mb-2">
                                        <School className="w-4 h-4 text-blue-500" />
                                        <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">School Districts</span>
                                      </div>
                                      {partnerData.districts.map(d => (
                                        <div key={d.id} className="mb-2 last:mb-0">
                                          <p className="text-sm font-semibold text-foreground px-2 mb-1">{d.name}</p>
                                          <div className="grid grid-cols-2 gap-0.5">
                                            {(d.schools || []).map(s => (
                                              <div key={s.id} className="text-xs text-muted-foreground px-3 py-1 rounded hover:bg-accent flex items-center gap-1.5">
                                                <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                                                {s.name}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {/* Park Districts */}
                                  {partnerData.parks.length > 0 && (
                                    <div className="mb-3 pt-2 border-t">
                                      <div className="flex items-center gap-2 px-1 mb-2">
                                        <Trees className="w-4 h-4 text-green-500" />
                                        <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Park Districts</span>
                                      </div>
                                      <div className="grid grid-cols-2 gap-0.5">
                                        {partnerData.parks.map(p => (
                                          <div key={p.id} className="text-xs text-muted-foreground px-3 py-1.5 rounded hover:bg-accent flex items-center gap-1.5">
                                            <div className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
                                            {p.name}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {/* Libraries */}
                                  <div className="pt-2 border-t">
                                    <div className="flex items-center gap-2 px-1 mb-2">
                                      <BookOpen className="w-4 h-4 text-purple-500" />
                                      <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Libraries</span>
                                    </div>
                                    <div className="grid grid-cols-1 gap-0.5">
                                      {headerLocations.filter(l => l.location_category === 'library').map(l => (
                                        <div key={l.id} className="text-xs text-muted-foreground px-3 py-1.5 rounded hover:bg-accent flex items-center gap-1.5">
                                          <div className="w-1.5 h-1.5 rounded-full bg-purple-400 flex-shrink-0" />
                                          {l.name}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  {/* CTA */}
                                  <div className="mt-3 pt-2 border-t">
                                    <Link to="/partnerships" className="flex items-center gap-2 text-xs font-semibold text-rsc-blue hover:underline px-2 py-1">
                                      <Handshake className="w-3.5 h-3.5" /> Become a Partner →
                                    </Link>
                                  </div>
                                </div>
                              ) : (
                              <ul className="grid gap-1 p-3 w-[320px]">
                                {(apiDropdownMenus[link.dropdownId] || dropdownMenus[link.dropdownId] || []).map((item) => (
                                  <li key={item.href + item.label}>
                                    <NavigationMenuLink asChild>
                                      <Link
                                        to={item.href}
                                        className="flex items-start gap-3 select-none rounded-lg px-3 py-2.5 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground group"
                                      >
                                        <div className="w-7 h-7 rounded-md bg-rsc-blue/10 flex items-center justify-center flex-shrink-0 group-hover:bg-rsc-blue/20 transition-colors">
                                          <item.icon className="w-3.5 h-3.5 text-rsc-blue" />
                                        </div>
                                        <div className="flex-1">
                                          <div className="text-sm font-semibold leading-none mb-1">{item.label}</div>
                                          {item.description && <p className="text-xs leading-snug text-muted-foreground">{item.description}</p>}
                                        </div>
                                      </Link>
                                    </NavigationMenuLink>
                                  </li>
                                ))}
                              </ul>
                              )}
                            </NavigationMenuContent>
                          </>
                        ) : (
                          <Link
                            to={link.href}
                            className={`flex items-center text-base font-medium no-underline transition-all duration-200 ${
                              link.highlight
                                ? `${pillClass} px-3 py-1 rounded-full whitespace-nowrap`
                                : `text-white/90 hover:text-white px-2 py-1`
                            } ${isActive ? 'ring-1 ring-white/30 text-white' : ''}`}
                            style={{ textDecoration: 'none' }}
                          >
                            {link.label}
                          </Link>
                        )}
                      </NavigationMenuItem>
                    );
                  })}
                </NavigationMenuList>
              </NavigationMenu>
            </div>

            {/* Right side — Dashboard + Auth + Cart */}
            <div className="hidden lg:flex items-center gap-1.5 flex-shrink-0">
              {isAuthenticated && (
                <Link
                  to={getDashboardLink()}
                  className={`flex items-center gap-1.5 text-base font-semibold px-4 py-1.5 rounded-full transition-all no-underline ${
                    location.pathname.startsWith('/parent') || location.pathname.startsWith('/admin') || location.pathname.startsWith('/instructor') || location.pathname.startsWith('/partner')
                      ? 'bg-rsc-yellow/20 text-rsc-yellow'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid="nav-dashboard"
                  style={{ textDecoration: 'none' }}
                >
                  <LayoutDashboard className="w-3.5 h-3.5" />
                  {getDashboardLabel()}
                </Link>
              )}
            </div>

            {/* Auth actions */}
            <div className="flex items-center gap-2 ml-auto">
              {isAuthenticated ? (
                <>
                  <Link to="/cart" className="relative" data-testid="cart-link">
                    <Button variant="ghost" size="sm" className="text-white hover:bg-white/10 h-8 w-8 p-0">
                      <ShoppingCart className="h-4 w-4" />
                      {itemCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 rounded-full bg-rsc-yellow text-rsc-navy text-[10px] flex items-center justify-center font-bold h-4 w-4">
                          {itemCount}
                        </span>
                      )}
                    </Button>
                  </Link>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="gap-1.5 text-white hover:bg-white/10 h-8 px-2" data-testid="user-menu-trigger">
                        <div className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center">
                          <User className="h-3.5 w-3.5" />
                        </div>
                        <span className="hidden md:inline text-sm">{user?.first_name}</span>
                        <ChevronDown className="h-3 w-3 opacity-60" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem asChild>
                        <Link to={getDashboardLink()} className="flex items-center gap-2" data-testid="dashboard-link">
                          <LayoutDashboard className="h-4 w-4" />
                          Dashboard
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={logout} 
                        className="text-destructive focus:text-destructive"
                        data-testid="logout-btn"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="ghost" size="sm" className="text-white/80 hover:text-white hover:bg-white/10 text-base h-10 px-4" data-testid="login-btn">Login</Button>
                  </Link>
                  <Link to="/auth/register" className="hidden sm:block">
                    <Button className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright font-bold rounded-full px-6 h-10 text-base shadow-md" data-testid="register-btn">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}

              {/* Mobile Menu */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/10 h-8 w-8 p-0" data-testid="mobile-menu-trigger">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80 bg-rsc-navy border-rsc-navy">
                  <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                  <div className="flex items-center gap-2 mb-8">
                    <Rocket className="w-5 h-5 text-rsc-yellow" />
                    <span className="font-heading font-bold text-white text-lg">Ready Set STEM</span>
                  </div>
                  <nav className="flex flex-col gap-1" role="navigation" aria-label="Mobile navigation">
                    {navLinks.map((link) => {
                      const isActive = location.pathname === link.href;
                      const hasDropdown = link.hasDropdown && dropdownMenus[link.dropdownId];
                      
                      if (hasDropdown) {
                        return (
                          <div key={link.href}>
                            <button
                              onClick={() => setMobileExpandedMenu(prev => prev === link.dropdownId ? null : link.dropdownId)}
                              className={`w-full text-left text-base font-medium py-2.5 px-3 rounded-lg transition-all flex items-center justify-between ${
                                isActive ? 'text-rsc-yellow bg-white/10' : 'text-white/80 hover:text-white hover:bg-white/5'
                              }`}
                            >
                              {link.label}
                              <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpandedMenu === link.dropdownId ? 'rotate-180' : ''}`} />
                            </button>
                            {mobileExpandedMenu === link.dropdownId && (
                              <div className="ml-3 mt-1 space-y-0.5 border-l-2 border-white/10 pl-3">
                                {dropdownMenus[link.dropdownId].map(item => (
                                  <Link
                                    key={item.href}
                                    to={item.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="block text-sm text-white/60 hover:text-white py-1.5 no-underline"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    <item.icon className="w-3.5 h-3.5 inline mr-2 opacity-50" />
                                    {item.label}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      }

                      return (
                        <Link
                          key={link.href}
                          to={link.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`text-base font-medium py-2.5 px-3 rounded-lg transition-all no-underline ${
                            isActive
                              ? 'text-rsc-yellow bg-white/10'
                              : 'text-white/80 hover:text-white hover:bg-white/5'
                          }`}
                          data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                          style={{ textDecoration: 'none' }}
                        >
                          {link.label}
                        </Link>
                      );
                    })}
                    {isAuthenticated && (
                      <Link
                        to={getDashboardLink()}
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-base font-semibold py-2.5 px-3 rounded-lg text-rsc-yellow bg-white/5 flex items-center gap-2 mt-2 no-underline"
                        data-testid="mobile-nav-dashboard"
                        style={{ textDecoration: 'none' }}
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        {getDashboardLabel()}
                      </Link>
                    )}
                    {!isAuthenticated && (
                      <Link to="/auth/register" onClick={() => setMobileMenuOpen(false)} className="mt-4">
                        <Button className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright w-full font-bold rounded-full" data-testid="mobile-register-btn">
                          Sign Up
                        </Button>
                      </Link>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}
